/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFocusWithin } from 'react-aria';

import {
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';
import { useSemicontrolledState } from '../_common/use-semi-controlled-state';
import { needleWarningMessage, randomId } from '../_common/utils';
import { IconButton } from '../icon-button';
import {
  CheckIconOutline,
  PencilIconOutline,
  XMarkIconOutline,
} from '../icons';
import { Typography } from '../typography';
import { type TypographyVariants } from '../typography';
import { getIconStyle, getMinHeight } from './utils';

type InlineEditProps = {
  /** Label text */
  label?: ReactNode;

  /**
   * The typographyVariant of the component applied to Typography as its variant.
   * @default subheading-medium
   */
  typographyVariant?: TypographyVariants;

  /**
   * The default input value. Use for an uncontrolled component.
   */
  defaultValue?: string;

  /**
   * The input value. Makes the component controlled.
   */
  value?: string;

  /**
   * Callback when the input value changes. Must be used when the component is controlled.
   */
  onChange?: (value: string) => void;

  /**
   * Placeholder text displayed when the input is empty.
   */
  placeholder: string;

  /**
   * @default false
   */
  isDisabled?: boolean;

  /**
   * If edit pencil icon should be visible on hover
   * @default false
   */
  hasEditIcon?: boolean;

  /**
   * If the input is in editing mode
   * Used in a stateless inline edit
   * @default undefined
   */
  isEditing?: boolean;

  /** Callback when confirm icon button is pressed */
  onConfirm?: (
    value: string,
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void;

  /** Callback when cancel icon button is pressed */
  onCancel?: (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void;

  /**
   * What the input should do on blur
   * - "confirm" will confirm the value
   * - "cancel" will cancel the value
   * - "none" will do nothing
   * @default "confirm"
   */
  blurBehavior?: 'confirm' | 'cancel' | 'none';

  /** props applied to the internal input element */
  inputProps?: HtmlAttributes<'input'>;

  /**
   * If the input is fluid
   * @default false
   */
  isFluid?: boolean;
};

// The input should not be less than this (it does shrink if container is smaller though).
const MINIMUM_WIDTH = 320;

export const InlineEdit = <T extends React.ElementType = 'div'>({
  as,
  className,
  defaultValue,
  htmlAttributes,
  label,
  onChange,
  onCancel,
  onConfirm,
  ref,
  style,
  value,
  hasEditIcon = false,
  inputProps,
  isDisabled = false,
  isEditing,
  blurBehavior = 'confirm',
  isFluid = false,
  typographyVariant = 'subheading-medium',
  placeholder,
  ...restProps
}: PolymorphicCommonProps<T, InlineEditProps>) => {
  const [hasEditMode, setEditMode] = useState(false);

  const [currentValue, setCurrentValue] = useSemicontrolledState({
    isControlled: value !== undefined,
    onChange,
    state: value ?? defaultValue ?? '',
  });
  const [previousConfirmedValue, setPreviousConfirmedValue] =
    useState(currentValue);

  const mirrorRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * This ref is used to prevent the blur behavior from being triggered on mouse down events
   * on the confirm and cancel buttons. So when true, the blur behavior is not triggered.
   * This is needed due to Webkit browsers (like Safari) triggering blur on mouse down events.
   *
   * It is also used to prevent the blur behavior from being triggered after the component exits edit mode without blur.
   * This bug caused the handler to be called when something was focused after confirming.
   */
  const blurWithinRef = useRef(false);

  // Validate controlled mode requirements
  useEffect(() => {
    if (value !== undefined && onChange === undefined) {
      needleWarningMessage(
        'onChange prop should be supplied when using controlled mode (value prop is provided).',
      );
    }
  }, [value, onChange]);

  const { focusWithinProps } = useFocusWithin({
    onBlurWithin: () => {
      if (blurWithinRef.current) {
        return;
      }

      if (blurBehavior === 'confirm') {
        onConfirmHandler();
        return;
      }
      if (blurBehavior === 'cancel') {
        onCancelHandler();
        return;
      }
    },
  });

  const resetToDefaults = useCallback(() => {
    // In controlled mode, don't reset the value - let the parent manage it
    // In uncontrolled mode, reset to default value
    if (value === undefined) {
      setCurrentValue(previousConfirmedValue ?? '');
    }
    // Only reset edit mode if isEditing is not controlled (undefined)
    if (isEditing === undefined) {
      blurWithinRef.current = true;
      setEditMode(false);
    }
  }, [previousConfirmedValue, isEditing, setCurrentValue, value]);

  const resetEditState = useCallback(() => {
    // Only reset edit mode if isEditing is not controlled (undefined)
    if (isEditing === undefined) {
      blurWithinRef.current = true;
      setEditMode(false);
    }
  }, [isEditing]);

  const onConfirmHandler = useCallback(
    async (
      event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
      setPreviousConfirmedValue(currentValue);
      onConfirm?.(currentValue, event);
      resetEditState();
    },
    [onConfirm, currentValue, resetEditState],
  );

  const onCancelHandler = useCallback(
    (
      event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ) => {
      onCancel?.(event);
      resetToDefaults();
    },
    [onCancel, resetToDefaults],
  );

  const inputID = useMemo(() => {
    return inputProps?.id ?? randomId(12);
  }, [inputProps?.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (['Enter', 'Escape'].includes(e.key)) {
        if (e.key === 'Enter') {
          onConfirmHandler();
        }
        if (e.key === 'Escape') {
          onCancelHandler();
        }
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [onConfirmHandler, onCancelHandler],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setCurrentValue(newValue);
    },
    [setCurrentValue],
  );

  const isInEditMode = isEditing ?? hasEditMode;

  const handleEditClick = useCallback(() => {
    if (isDisabled === false) {
      setEditMode(true);
      blurWithinRef.current = false;
    }
  }, [isDisabled]);

  const updateInputWidth = useCallback(() => {
    if (mirrorRef.current && containerRef.current && !isFluid) {
      const mirrorWidth = mirrorRef.current.offsetWidth;
      const newWidth = Math.max(mirrorWidth, MINIMUM_WIDTH);
      containerRef.current.style.width = `${newWidth}px`;
    }
  }, [mirrorRef, containerRef, isFluid]);

  // Width calculation effect
  useLayoutEffect(() => {
    updateInputWidth();
  }, [updateInputWidth, isInEditMode, currentValue]);

  const displayText = currentValue || placeholder;

  return (
    <Typography
      as={as ?? 'div'}
      variant={typographyVariant}
      className={classNames('ndl-inline-edit', className, {
        'ndl-disabled': isDisabled,
        'ndl-fluid': isFluid,
      })}
      style={style}
      ref={ref}
      htmlAttributes={htmlAttributes}
      {...restProps}
    >
      {Boolean(label) && <label htmlFor={inputID}>{label}</label>}
      {isInEditMode === true ? (
        <div
          className="ndl-inline-edit-container"
          ref={containerRef}
          {...focusWithinProps}
        >
          {/* Mirror span for measuring width */}
          <span
            aria-hidden={true}
            className="ndl-inline-edit-mirror"
            ref={mirrorRef}
          >
            {displayText}
          </span>
          <input
            {...inputProps}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label={typeof label === 'string' ? label : 'Inline edit input'}
            className={classNames(
              'ndl-inline-edit-input',
              inputProps?.className,
            )}
            id={inputID}
            value={currentValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
          <div className="ndl-inline-edit-buttons">
            <IconButton
              description="Accept"
              tooltipProps={{
                root: { placement: 'bottom' },
              }}
              onClick={onConfirmHandler}
              size="small"
              isFloating={true}
              htmlAttributes={{
                onMouseDown: () => {
                  blurWithinRef.current = true;
                },
              }}
            >
              <CheckIconOutline />
            </IconButton>
            <IconButton
              description="Cancel"
              tooltipProps={{
                root: { placement: 'bottom' },
              }}
              onClick={(event) => onCancelHandler(event)}
              size="small"
              isFloating={true}
              htmlAttributes={{
                onMouseDown: () => {
                  blurWithinRef.current = true;
                },
              }}
            >
              <XMarkIconOutline />
            </IconButton>
          </div>
        </div>
      ) : (
        <button
          className={classNames('ndl-inline-idle-container', {
            'n-text-neutral-text-weaker': displayText === placeholder,
            'ndl-disabled': isDisabled,
            'ndl-has-edit-icon': hasEditIcon,
          })}
          aria-disabled={isDisabled}
          disabled={isDisabled}
          onClick={handleEditClick}
          style={{
            minHeight: getMinHeight(typographyVariant),
          }}
        >
          <span className="ndl-inline-edit-text">{displayText}</span>
          {hasEditIcon && (
            <PencilIconOutline
              className="ndl-inline-edit-pencil-icon"
              style={getIconStyle(typographyVariant)}
            />
          )}
        </button>
      )}
    </Typography>
  );
};
